import { planAnalysis, validateAnalysisState, type QueryPlan } from '@aibi/analysis-engine';
import {
  AI_ACTION_TYPES,
  type AIAction,
  type AIActionType,
  type AnalysisState,
} from '@aibi/contracts';
import { collectExpressionReferences, type SemanticModelIndex } from '@aibi/semantic-model';
import { err, ok, type Result } from '@aibi/shared';
import { applyAIActions, type AppliedAIActions } from '../apply/apply-ai-actions.js';
import { measureExpression } from '../calculated/validate-calculated.js';
import type { AIContext } from '../context/ai-context.js';
import type { AIActionError, AICandidate, AIClarification, AIDiagnostic } from '../errors.js';
import { DEFAULT_AI_CORE_LIMITS, type AICoreLimits } from '../limits.js';
import type { AIModelOutput } from '../protocol.js';
import { findActionReferenceIssues } from '../validate-ai-response.js';
import { diagnostic as diag, parseAIModelOutput } from './parse-output.js';

export interface AIValidationInput {
  /** Texto devuelto por el proveedor. Nunca se ejecuta: solo `JSON.parse` + esquema. */
  readonly raw: string;
  readonly context: AIContext;
  readonly index: SemanticModelIndex;
  /** Estado sobre el que se simulan las acciones; no se modifica. */
  readonly state: AnalysisState;
  /** Lista blanca de acciones; por defecto, todo el catalogo AI Action. */
  readonly allowedActions?: readonly AIActionType[];
  readonly limits?: AICoreLimits;
}

export type ValidatedAIOutput =
  | {
      readonly kind: 'actions';
      readonly actions: readonly AIAction[];
      readonly rationale?: string;
      readonly applied: AppliedAIActions;
      /** Plan del estado resultante; `null` si ninguna accion modifica el estado. */
      readonly plan: QueryPlan | null;
    }
  | { readonly kind: 'clarification'; readonly clarification: AIClarification }
  | { readonly kind: 'error'; readonly error: AIActionError };

/**
 * Pipeline de validacion de una respuesta de IA, por etapas y fail-fast:
 * tamano -> JSON -> guardrails -> esquema -> acciones (listas blancas y limites) ->
 * semantica (modelo) -> sandbox atomico -> Analysis State -> Analysis Engine (plan).
 * Una respuesta que no supera TODAS las etapas no produce ningun cambio.
 *
 * @example
 * const validated = validateAIOutput({ raw, context, index, state: card });
 * if (!validated.ok) showDiagnostics(validated.error);
 */
export function validateAIOutput(
  input: AIValidationInput,
): Result<ValidatedAIOutput, AIDiagnostic[]> {
  const limits = input.limits ?? DEFAULT_AI_CORE_LIMITS;
  const allowed = new Set<AIActionType>(input.allowedActions ?? AI_ACTION_TYPES);

  // 1-4. Tamano, JSON, guardrails y esquema estricto del protocolo
  const parsed = parseAIModelOutput(input.raw, limits);
  if (!parsed.ok) return parsed;
  const output = parsed.value;

  if (output.kind !== 'actions') return validateNonActionOutput(output, input.context);

  // 5. Acciones: limites y listas blancas
  const actionIssues = checkActions(output.actions, input.context, allowed, limits);
  if (actionIssues.length > 0) return err(actionIssues);

  // 6. Semantica contra el Semantic Model
  const semanticIssues = output.actions.flatMap((action, i) =>
    findActionReferenceIssues(action, input.index).map((message) =>
      diag('semantic', 'SEMANTIC_ERROR', `actions.${String(i)}.payload`, message, action.id),
    ),
  );
  if (semanticIssues.length > 0) return err(semanticIssues);

  // 7. Sandbox atomico sobre una copia del estado
  const applied = applyAIActions(input.state, output.actions, input.index);
  if (!applied.ok) return err(applied.error);

  let plan: QueryPlan | null = null;
  if (applied.value.stateActionIds.length > 0) {
    // 8. Analysis State resultante
    const validated = validateAnalysisState(applied.value.state, input.index);
    if (!validated.ok) {
      return err(
        validated.error.details.map((detail) =>
          diag('analysis-state', 'STATE_INVALID', detail.path, detail.message),
        ),
      );
    }
    // 9. Analysis Engine: el estado debe poder planificarse
    const planned = planAnalysis(validated.value, input.index.model);
    if (!planned.ok) {
      const errors = planned.error.diagnostics.filter((d) => d.severity === 'error');
      return err(
        (errors.length > 0 ? errors : planned.error.diagnostics).map((d) =>
          diag('analysis-engine', 'STATE_INVALID', d.path ?? '', d.message),
        ),
      );
    }
    plan = planned.value.plan;
  }

  return ok({
    kind: 'actions',
    actions: output.actions,
    ...(output.rationale === undefined ? {} : { rationale: output.rationale }),
    applied: applied.value,
    plan,
  });
}

/** Ids del modelo que una accion referencia y que deben estar en el contexto de IA. */
function referencedObjects(action: AIAction): { fields: string[]; hierarchies: string[] } {
  const fields: string[] = [];
  const hierarchies: string[] = [];
  switch (action.type) {
    case 'addDimension':
    case 'removeDimension':
      fields.push(action.payload.dimensionId);
      break;
    case 'addMeasure':
    case 'removeMeasure':
      fields.push(action.payload.measureId);
      break;
    case 'addFilter': {
      const { target } = action.payload.filter;
      fields.push(target.kind === 'dimension' ? target.dimensionId : target.measureId);
      break;
    }
    case 'sort':
      for (const { target } of action.payload.by) {
        fields.push(target.kind === 'dimension' ? target.dimensionId : target.measureId);
      }
      break;
    case 'resetSelections':
      if (action.payload.dimensionId !== undefined) fields.push(action.payload.dimensionId);
      break;
    case 'requestExplanation':
      if (action.payload.measureId !== undefined) fields.push(action.payload.measureId);
      break;
    case 'comparePeriods':
      if (action.payload.comparison.dateDimensionId !== undefined) {
        fields.push(action.payload.comparison.dateDimensionId);
      }
      break;
    case 'drillDown':
    case 'drillUp':
      hierarchies.push(action.payload.hierarchyId);
      break;
    case 'createMeasure':
      if ('expression' in action.payload.measure) {
        fields.push(...collectExpressionReferences(action.payload.measure.expression).measureIds);
      }
      break;
    case 'createDimension':
    case 'createCalculatedColumn':
    case 'removeFilter':
    case 'updateFilter':
    case 'clearFilters':
    case 'setLimit':
    case 'setVisualization':
    case 'suggestVisualization':
    case 'updateGrouping':
    case 'resetAnalysis':
    case 'narrative':
      break;
  }
  return { fields, hierarchies };
}

function checkActions(
  actions: readonly AIAction[],
  context: AIContext,
  allowed: ReadonlySet<AIActionType>,
  limits: AICoreLimits,
): AIDiagnostic[] {
  if (actions.length > limits.maxActionsPerResponse) {
    return [
      diag(
        'action',
        'LIMIT_EXCEEDED',
        'actions',
        `La respuesta propone mas de ${String(limits.maxActionsPerResponse)} acciones.`,
      ),
    ];
  }
  const visibleFields = new Set(context.fields.map((field) => field.id));
  const visibleHierarchies = new Set(context.hierarchies.map((hierarchy) => hierarchy.id));
  const visualizations = new Set(context.visualizations.map((viz) => viz.type));
  const issues: AIDiagnostic[] = [];
  const seen = new Set<string>();
  actions.forEach((action, i) => {
    const path = `actions.${String(i)}`;
    if (seen.has(action.id))
      issues.push(
        diag(
          'action',
          'INVALID_SCHEMA',
          `${path}.id`,
          `Id de accion duplicado "${action.id}".`,
          action.id,
        ),
      );
    seen.add(action.id);
    if (!allowed.has(action.type)) {
      issues.push(
        diag(
          'action',
          'ACTION_NOT_ALLOWED',
          `${path}.type`,
          `La accion "${action.type}" no esta permitida.`,
          action.id,
        ),
      );
      return;
    }
    const { fields, hierarchies } = referencedObjects(action);
    for (const id of fields) {
      if (!visibleFields.has(id)) {
        issues.push(
          diag(
            'action',
            'ACTION_NOT_ALLOWED',
            `${path}.payload`,
            `El campo "${id}" no esta disponible para el asistente.`,
            action.id,
          ),
        );
      }
    }
    for (const id of hierarchies) {
      if (!visibleHierarchies.has(id)) {
        issues.push(
          diag(
            'action',
            'ACTION_NOT_ALLOWED',
            `${path}.payload`,
            `La jerarquia "${id}" no esta disponible para el asistente.`,
            action.id,
          ),
        );
      }
    }
    if (
      (action.type === 'setVisualization' || action.type === 'suggestVisualization') &&
      !visualizations.has(action.payload.type)
    ) {
      issues.push(
        diag(
          'action',
          'ACTION_NOT_ALLOWED',
          `${path}.payload.type`,
          `La visualizacion "${action.payload.type}" no esta disponible.`,
          action.id,
        ),
      );
    }
    const expression =
      action.type === 'createMeasure' && 'expression' in action.payload.measure
        ? action.payload.measure.expression
        : action.type === 'createDimension' && 'expression' in action.payload.dimension
          ? action.payload.dimension.expression
          : action.type === 'createCalculatedColumn'
            ? action.payload.column.expression
            : undefined;
    if (expression) {
      const { depth, nodes } = measureExpression(expression);
      if (depth > limits.maxExpressionDepth || nodes > limits.maxExpressionNodes) {
        issues.push(
          diag(
            'action',
            'LIMIT_EXCEEDED',
            `${path}.payload`,
            'La expresion propuesta es demasiado grande.',
            action.id,
          ),
        );
      }
    }
  });
  return issues;
}

function validateNonActionOutput(
  output: Exclude<AIModelOutput, { kind: 'actions' }>,
  context: AIContext,
): Result<ValidatedAIOutput, AIDiagnostic[]> {
  const visibleFields = new Set(context.fields.map((field) => field.id));
  const visualizations = new Set<string>(context.visualizations.map((viz) => viz.type));
  const filters = new Set(context.analysis?.filters.map((filter) => filter.id) ?? []);
  const candidates: AICandidate[] = (output.candidates ?? []).map((candidate) => ({
    id: candidate.id,
    label: candidate.label,
    kind: candidate.kind,
    ...(candidate.dimensionId === undefined ? {} : { dimensionId: candidate.dimensionId }),
  }));
  const issues = candidates.flatMap((candidate, i): AIDiagnostic[] => {
    const known =
      candidate.kind === 'visualization'
        ? visualizations.has(candidate.id)
        : candidate.kind === 'filter'
          ? filters.has(candidate.id)
          : candidate.kind === 'member'
            ? visibleFields.has(candidate.dimensionId ?? candidate.id)
            : visibleFields.has(candidate.id);
    return known
      ? []
      : [
          diag(
            'action',
            'ACTION_NOT_ALLOWED',
            `candidates.${String(i)}`,
            `Candidato desconocido "${candidate.id}".`,
          ),
        ];
  });
  if (issues.length > 0) return err(issues);
  if (output.kind === 'clarification') {
    return ok({
      kind: 'clarification',
      clarification: {
        question: output.question,
        reason: output.reason,
        candidates,
        affectedActionIds: output.affectedActionIds,
        ...(output.term === undefined ? {} : { term: output.term }),
      },
    });
  }
  return ok({
    kind: 'error',
    error: {
      code: output.code,
      message: output.message,
      attemptedAction: { type: 'unknown' },
      ...(candidates.length === 0 ? {} : { candidates }),
    },
  });
}
